/**
 * Options shown in the From/To pickers before the user types anything.
 *
 * These used to come from a hardcoded POPULAR_CITIES list (Mumbai, Bangalore,
 * Hyderabad - none of which are valid city slugs or have a CMS page) and from
 * getPopularCitiesInState, which returns census place names in the same state
 * and produced entries like "NewDelhi", "AurangabadMaharashtra" and
 * "SangliMirajKupwad". Picking one led to a page that does not exist.
 *
 * Everything here now comes from src/data/cities.json - the 439 cities that
 * return a real page from get_city_pages - and, for the To field, from
 * tier1Routes.json so the resulting search lands on a route page we publish.
 * Google Places still handles free-text addresses; this is only the instant
 * list shown on click.
 */

import cities from "../data/cities.json"
import tier1Routes from "../data/tier1Routes.json"
import { slugify } from "./slugify"
import { getRecentSearchesForField } from "./recentSearches"

const BY_SLUG = new Map(cities.map((c) => [c.slug, c]))

// Big metros first, then the destinations people search for most. Every slug
// here is verified present in cities.json.
const POPULAR_SLUGS = [
  "delhi",
  "bengaluru",
  "pune",
  "jaipur",
  "chennai",
  "kolkata",
  "ahmedabad",
  "lucknow",
  "chandigarh",
  "agra",
  "secunderabad",
  "coimbatore",
]

const toOption = (slugOrName) => {
  const slug = BY_SLUG.has(slugOrName) ? slugOrName : slugify(slugOrName)
  const entry = BY_SLUG.get(slug)
  if (!entry) return null
  return {
    label: entry.name,
    value: {
      // ", India" keeps Google Places matching sane if this value is later
      // geocoded for lat/long.
      description: `${entry.name}, India`,
      place_id: null,
    },
    city: entry.name,
    slug: entry.slug,
  }
}

/**
 * Convert a city name to the option shape the pickers use.
 * Kept async because callers already await it.
 */
export const cityNameToPlaceOption = async (cityName) => {
  if (!cityName) return null
  const known = toOption(cityName)
  if (known) return known
  // Not a city we publish - still usable as free text so booking is never
  // blocked by our own coverage gaps.
  return {
    label: cityName,
    value: {
      description: cityName.includes(",") ? cityName : `${cityName}, India`,
      place_id: null,
    },
    city: cityName,
  }
}

const pushUnique = (options, candidates, limit) => {
  for (const opt of candidates) {
    if (options.length >= limit) return
    if (!opt) continue
    if (options.some((o) => o.label === opt.label)) continue
    options.push(opt)
  }
}

/**
 * Default options for a field.
 *
 * Synchronous on purpose. react-select's useAsync copies `defaultOptions` into
 * state on its FIRST render and never re-reads the prop, so a list that arrives
 * from an async call after mount is silently ignored and the menu stays empty.
 * Callers must have the list ready for the initial render.
 *
 * @param {'from'|'to'} field
 * @param {string} currentCity  detected or currently selected city
 * @param {string} fromCity     the chosen "from", used to suggest destinations
 */
export const getDefaultOptionsSync = (field, currentCity = null, fromCity = null) => {
  const options = []
  const LIMIT = 12

  // 1. What this user searched before.
  pushUnique(options, getRecentSearchesForField(field), 5)

  // 2. For the To field, the destinations actually reachable from the chosen
  //    origin. These are Tier-1 routes, so the search lands on a real page.
  if (field === "to" && fromCity) {
    const dests = tier1Routes[slugify(fromCity)] || []
    pushUnique(options, dests.map(toOption), LIMIT)
  }

  // 3. The user's own city is the most likely pickup.
  if (field === "from" && currentCity) {
    pushUnique(options, [toOption(currentCity)], LIMIT)
  }

  // 4. Fall back to the big ones.
  pushUnique(options, POPULAR_SLUGS.map(toOption), LIMIT)

  return options.slice(0, LIMIT)
}

/** Async wrapper: several callers already await this. */
export const getDefaultOptions = async (field, currentCity = null, fromCity = null) =>
  getDefaultOptionsSync(field, currentCity, fromCity)

/**
 * Cities matching what the user has typed, ranked so a prefix match beats a
 * mid-string one. Lets the picker answer instantly instead of waiting on the
 * Google Places round trip.
 */
export const searchCities = (query, limit = 12) => {
  const q = String(query || "").trim().toLowerCase()
  if (!q) return []
  const starts = []
  const contains = []
  for (const c of cities) {
    const name = c.name.toLowerCase()
    if (name.startsWith(q)) starts.push(c)
    else if (name.includes(q)) contains.push(c)
  }
  return [...starts, ...contains].slice(0, limit).map((c) => toOption(c.slug))
}
